import { DocumentBasic, DocumentBlank, MediaCamera, MediaHeadphones, NoticeInfo } from '@fs/zion-icon'
import { DocumentStory, MenuNewWindow } from '@fs/zion-icon/dist/cjs/icons'
import { ActionChip, IconButton, ResponsiveTableRow, Row } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { toLocaleString } from '../../../util/DateUtil'
import { PermissionsContext } from '../../app/PermissionsContext'
import { ARTIFACT_CATEGORY, CONTENT_CATEGORY, SCREENING_STATE } from '../../artifacts/util/artifactsConstants'
import { getMemoriesUrl, truncateString } from '../../artifacts/util/artifactsUtils'

export const MAX_TITLE_LENGTH = 160
export const MAX_DESCRIPTION_LENGTH = 160

/**
 * A TableRow with information about the given artifact, to be used in PersonMemories table
 */
const ArtifactRow = ({ artifact, ...otherProps }) => {
  const [t] = useTranslation()
  const restricted = artifact.screeningState === SCREENING_STATE.RESTRICTED
  const { isAuthorized } = useContext(PermissionsContext)

  return (
    <ResponsiveTableRow
      {...otherProps}
      column1={<ArtifactIcon category={artifact.category} contentCategory={artifact.contentCategory} />}
      column2={truncateString(artifact.title, MAX_TITLE_LENGTH)}
      column3={truncateString(artifact.description, MAX_DESCRIPTION_LENGTH)}
      column4={toLocaleString(artifact.uploadDatetime, false)}
      column5={
        <>
          {artifact.id}
          {restricted && (
            <>
              {' '}
              <ActionChip inline label={t('accessControl.restricted', 'Restricted')} />
            </>
          )}
        </>
      }
      column6={
        <Row alignX="end">
          <IconButton
            Icon={MenuNewWindow}
            title={t('artifacts.viewInMemories', 'View in Memories')}
            to={getMemoriesUrl(artifact.id)}
            target="_blank"
          />
          {isAuthorized('ArtPrmCanReview') && (
            <IconButton
              Icon={NoticeInfo}
              title={t('artifacts.artifactInfo', 'Artifact Info')}
              to={`/artifacts/artifact/${artifact.id}`}
              target="_blank"
            />
          )}
        </Row>
      }
    />
  )
}

ArtifactRow.propTypes = {
  artifact: PropTypes.shape({
    id: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
    screeningState: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    uploadDatetime: PropTypes.number.isRequired,
  }).isRequired,
}

/**
 * A Zion icon corresponding to the given artifact type.
 * Uses an interesting combination of category and contentCategory
 */
function ArtifactIcon({ category, contentCategory }) {
  if (contentCategory === CONTENT_CATEGORY.PHOTO) {
    return <MediaCamera />
  }
  if (contentCategory === CONTENT_CATEGORY.DOCUMENT) {
    return <DocumentBasic />
  }
  if (category === ARTIFACT_CATEGORY.AUDIO) {
    return <MediaHeadphones />
  }
  if (category === ARTIFACT_CATEGORY.TEXT) {
    return <DocumentStory />
  }
  return <DocumentBlank />
}

export default ArtifactRow
